/** @jsx jsx */
import {
  Colors,
  Icon,
  Spacer,
  TagLabel,
  FloatingBox,
  Typography,
} from '@zapier/design-system';
import InAppSidebarSubheaderButton from './InAppSidebarSubheaderButton';
import { Fragment, useRef, useState } from 'react';
import { css, jsx } from '@emotion/react';

import { useUniversalNavContext } from '../../../hooks/useUniversalNavContext';
import { SidebarV3 } from './types';
import { emitUserInteractionEvent } from '../../../utils/avroEvents';

const MENU_WIDTH = '240px';
const MenuStyles = css({
  display: 'flex',
  flexDirection: 'column',
  maxHeight: 'inherit',
  boxSizing: 'border-box',
  overflow: 'auto',
  padding: 'var(--zds-space-8, 8px)',
  width: MENU_WIDTH,
  left: '3px',
  top: '30px',
  outline: 'none',
});

const MenuItemStyles = css({
  display: 'flex',
  gap: 'var(--zds-space-12, 12px)',
  padding: 'var(--zds-space-8, 8px)',
  borderRadius: '4px',
  cursor: 'pointer',
  position: 'relative',
  alignItems: 'center',
  '&:focus-visible, &:hover': {
    backgroundColor: 'var(--zds-ui-brand-weakest, #FDF5F2)',
  },
  '&:focus-visible': {
    outline: 'var(--zds-border-focus, 2px solid #695BE8)',
    outlineOffset: 'var(--zds-space-2, 2px)',
  },
});

const IconWrapperStyles = (isActive: boolean) =>
  css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'var(--zds-space-8, 8px)',
    borderRadius: '4px',
    backgroundColor: isActive
      ? 'var(--zds-ui-brand-weakest, #FDF5F2)'
      : 'var(--zds-background-strong, #F5F3EB)',

    '& svg': {
      fill: isActive ? `${Colors.zapier}` : 'var(--zds-text-default, #413735)',
    },
  });

const TitleWrapperStyles = css({
  font: `var(--zds-body-1, 400 14px/24px ${Typography.base.fontFamily})`,
  color: 'var(--zds-text-default, #413735)',
});

const DescriptionWrapperStyles = css({
  font: `var(--zds-minimal-print-1, 400 12px/20px ${Typography.base.fontFamily})`,
  color: 'var(--zds-text-weaker, #574E4C)',
});

type CreationItemProps = SidebarV3.CreateOption;

const CreationItem = ({
  description,
  href,
  icon,
  iconFilled,
  id,
  tag,
  target,
  title,
}: CreationItemProps) => {
  const [isActive, setIsActive] = useState(false);
  const { getFullyQualifiedURL } = useUniversalNavContext();

  return (
    <a
      css={MenuItemStyles}
      href={getFullyQualifiedURL(href)}
      id={id}
      target={target}
      onFocus={() => setIsActive(true)}
      onBlur={() => setIsActive(false)}
      onMouseEnter={() => setIsActive(true)}
      onMouseLeave={() => setIsActive(false)}
      onClick={() => {
        emitUserInteractionEvent({
          event_action: 'click',
          option_selected: `UniversalSidebar CreateDropdown: ${title}`,
        });
      }}
      data-zds
    >
      <div data-zds css={IconWrapperStyles(isActive)}>
        <Icon
          name={isActive ? iconFilled : icon}
          size={20}
          shouldAnimateFill={false}
        />
      </div>
      <div
        css={{
          display: 'flex',
          flexDirection: 'column',
        }}
        data-zds
      >
        <div css={{ display: 'flex', alignItems: 'center' }} data-zds>
          <span data-zds css={TitleWrapperStyles}>
            {title}
          </span>
          {tag && (
            <Fragment>
              <Spacer width={8} />
              <TagLabel size="small" color="simple">
                {tag}
              </TagLabel>
            </Fragment>
          )}
        </div>
        <span data-zds css={DescriptionWrapperStyles}>
          {description}
        </span>
      </div>
    </a>
  );
};

type CreateDropdownProps = {
  isCollapsed: boolean;
  items: SidebarV3.CreateOption[];
};

export const CreateDropdown = ({ isCollapsed, items }: CreateDropdownProps) => {
  const { isCreateMenuOpen, setIsCreateMenuOpen } = useUniversalNavContext();
  const buttonRef = useRef<HTMLDivElement>(null);

  return (
    <div ref={buttonRef} style={{ position: 'relative' }} data-zds>
      <InAppSidebarSubheaderButton
        iconName="formAdd"
        isCollapsed={isCollapsed}
        onClick={(event) => {
          // Stopping propagation in order to keep the sidebar from closing when clicking on the Create `button` element.
          event.stopPropagation();
          emitUserInteractionEvent({
            event_action: 'click',
            option_selected: `UniversalSidebar CreateDropdown ${
              isCreateMenuOpen ? 'close' : 'open'
            }`,
          });

          setIsCreateMenuOpen(!isCreateMenuOpen);
        }}
        text="Create"
      />

      {isCreateMenuOpen && (
        <FloatingBox
          borderColor="transparent"
          borderRadius={4}
          maxWidth={MENU_WIDTH}
          onClickOutside={(event) => {
            // exclude the button from this click handler
            if (
              buttonRef &&
              buttonRef.current &&
              !buttonRef.current.contains(event.target as HTMLElement)
            )
              setIsCreateMenuOpen(false);
          }}
        >
          <div css={MenuStyles} data-zds>
            {items.map((item) => (
              <CreationItem {...item} key={item.id} />
            ))}
          </div>
        </FloatingBox>
      )}
    </div>
  );
};
